<template>
  <div>
    <form @submit.prevent="validate">
      <div v-for='(field, index) in mapFields'
        :data-field='field.key'
        :key='hash(field)'
        class='form-group list'
      >
        <component
          :is='field.type'
          :field-label='field.label'
          :type='field.nativeType'
          :maxlength='field.maxLength'
          placeholder=' '
          :required='field.required'
          :optional='field.optional'
          :error='$v.FormModel[field.key] && $v.FormModel[field.key].$error'
          :helptip-header='``'
          :helptip-body='validationMessage(field)'
          :options='fieldOptions(field)'
          :track-by='"text"'
          :button-label='"Upload File"'
          :button-position='"bottom"'
          :post-action='api.env + field.dataSource.url'
          :headers='headers'
          :upload-auto='true'
          :multiple='multiple(field)'
          :readonly='field.type === FIELD_TYPES.file ? true : false'
          :show-helptip-icon='false'
          :endpoint='field.type === FIELD_TYPES.file ? field.dataSource.url : null'
          :openEndpoint='field.dataSource.url'
          @open='fetchOptions(field)'
          @select='() => { counter++; clearDependencies(field) }'
          @input='onChange'
          @hook:created='update(field)'
          @hook:beforeCreate='beforeCreateField(field)'
          label='text'
          v-model='FormModel[field.key]'
          v-show='shouldBeVisible(field)'
          class='full-width list'
          ref='fields'
          v-bind='meta(field.key, index, field)'
          :integer='field.integer'
        />
        <div v-if='shouldShowInfo(field).label' class='alternate'>
          <label>
            {{ field.label }}
            <em class='optional-field-label' v-if='!field.required'>&nbsp;- optional</em>
          </label>
          <div class='info-field'>
            <em>Select <strong>{{ shouldShowInfo(field).label }}</strong> {{ shouldShowInfo(field).meta }}</em>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>


<script>

import { required } from 'vuelidate/lib/validators'

const FIELD_TYPES = {
  number: 'ed-input',
  textarea: 'ed-textarea',
  text: 'ed-input',
  email: 'ed-input',
  select: 'ed-select',
  date: 'ed-datepicker',
  file: 'ed-upload'
}

const opIn = (value, collection) => !!~collection.indexOf(value)
const opNotIn = (...args) => !opIn(...args)

const operators = {
  'IN': opIn,
  'NOT IN': opNotIn
}

const fieldMapper = field => ({
  ...(field.constructor.name === 'Object' ? field : {}),

  type: FIELD_TYPES[field.type] || field.type || FIELD_TYPES['text'],
  nativeType: field.type === 'number' ? 'text' : field.type || 'text',
  key: field.name || field.key || (field.label || field).replace && (field.label || field).replace(/ /g, ''),
  label: field.label || field,
  required: field.required || (field.validation || {}).required,
  dependency: (field.validation || {}).dependencies[0],
  integer: field.type === 'number'
})

const TEMPLATE_FIELD = 'templateName'

export default {
  name: 'customFields',

  props: {
    endpoint: String,
    queryParameters: Object,
    values: Array
  },

  created() {
    this.getCustomFields()
  },

  data () {
    return {
      FormModel: {},
      formSettings: {},
      FIELD_TYPES,
      //
      headers: {},
      submitAction: '',
      canSubmit: true,
      options: {},
      sentInvite: false,
      showCancelBtn: false,
      formFields: [],
      counter: 0,
      loading: false,
      lastUpdatedKey: '',

      hash: (field) => {
        const hash = field.key + field.validation.dependencies.map(d => (this.FormModel[d.name] || {}).text).join('-') + this.counter
        // field.hash = field.hash || hash
        return hash
      },

      shouldBeVisible: (field) => {
        if (this.loading) return false
        const dependencies = !!field.validation.dependencies.length
        field.validation.unresolvedDependencies = []

        return !dependencies || field.validation.dependencies.some(dependency => {
          const dependsOn = this.FormModel[dependency.name]
          const dependsOnValue = (dependsOn || {}).text
          const collection = dependency.values

          const resolved = operators[dependency.operator](dependsOnValue, collection) || !collection.length

          if (resolved) {
            field.validation.unresolvedDependencies.push(dependency.name)
          }

          return dependsOnValue && resolved
        })
      },

      shouldShowInfo: (field) => {
        if (this.loading) return false
        let info = ''

        if (field.type === FIELD_TYPES.file) {
          const dependsOn = field.validation.unresolvedDependencies[0]
          const dependsOnLabel = (this.mapFields.find(customField => customField.name === dependsOn) || {}).label
          const dependsOnValue = dependsOn && (this.FormModel[dependsOn] || {}).text

          info = !dependsOnValue && {
            label: dependsOnLabel,
            meta: dependsOn === (field.validation.dependencies[0] || {}).name ? 'first' : ''
          }
        }

        return info
      },

      validationMessage: field => this.$v.FormModel[field.key] && this.$v.FormModel[field.key].$error
        ? field.description || `Please fill in ${this.$v.FormModel[field.key].validationRule === false && this.$v.FormModel[field.key].$model ? 'a valid' : 'the'} ${field.label}`
        : field.description
    }
  },

  validations() {
    return {
      FormModel: {
        ...this.validationRules
      }
    }
  },

  computed: {
    mapFields() {
      return this.formFields.filter(f => !f.hidden).map(fieldMapper)
    },

    validationRules() {
      const validations = {}
      this.mapFields.forEach(field => {
        validations[field.key] = field.required
          ? {
            isValid: v => !this.shouldBeVisible(field) ? true : required(v)
          } : {}
      })
      return validations
    }
  },

  methods: {
    getQueryString() {
      let params = this.queryParameters
      let q = ''
      if (Object.keys(params).length > 0) {
        q = '?' + Object.keys(params)
          .map(key => key + '=' + params[key])
          .join('&')
      }
      return q
    },

    getCustomFields() {
      let q = this.getQueryString()
      this.api.get(this.endpoint + q, {})
        .then(r => {
          if (r.data && r.data.form) {
            this.formSettings = r.data.form
            this.submitAction = this.formSettings.action

            if (this.formSettings.fieldsets && this.formSettings.fieldsets[0]) {
              this.formFields = this.formSettings.fieldsets[0].fields
              this.prefillCustomFields(this.formFields)
            }
          }
          this.$emit('loaded', {})
        })
    },

    prefillCustomFields(fields) {
      fields.map(field => {
        // update field.values with last saved data
        if (this.values) {
          let savedField = this.values.filter(i => i.key === field.key)
          if (savedField && savedField[0] &&
            typeof savedField[0] !== 'undefined') {
            if (field.values.length === 0) {
              field.values.push(savedField[0].value)
            } else {
              field.values[0] = savedField[0].value
            }
          }
        }
        // populate model
        if (field.values.length) {
          if (field.type === 'select') {
            if (field.name !== TEMPLATE_FIELD) {
              this.FormModel[field.name] = {
                value: 0,
                text: field.values[0]
              }
            }
          } else {
            this.FormModel[field.name] = field.values[0]
          }
        }
      })
    },

    meta(fieldKey, index) {
      const hasTemplateField = this.formFields.find(f => f.name === TEMPLATE_FIELD)

      if (fieldKey !== TEMPLATE_FIELD && hasTemplateField && !this.FormModel[TEMPLATE_FIELD]) {
        this.canSubmit = false
        return {
          disabled: true,
          class: 'dim disabled'
        }
      } else {
        if (this.FormModel[TEMPLATE_FIELD]) {
          this.canSubmit = true
        }
      }
    },

    showConfirmation() {
      this.FormModel.Email &&
      this.$notifications.notify({
        message: `An invitation has been sent to ${this.FormModel.SupplierName || ''} (${this.FormModel.Email})`,
        type: 'info',
        placement: { from: 'top', align: 'center' }
      })
    },

    cancel() {
      this.emptySupplier(this.FormModel)
      document.querySelector('.main-panel').scrollTo(0, 0)
      this.$v.$reset()
    },

    emptySupplier(obj) {
      const fields = [...this.mapFields]
      fields.forEach(field => {
        if (field.key) {
          obj[field.key] = null
        }
      })
      obj.MeetingDate = null
    },

    async fetchOptions(field, i) {
      if (this.options[field.key] && this.options[field.key].length && !field.dataSource.queryParameters.length) return
      const options = await this.api.get(field.dataSource.url + '?' + this.query(field, this.FormModel))
      options && options.data
        ? this.options[field.key] = options.data
        : void 0
    },

    update(field) {
      if (this.options[field.key]) {
      }
    },

    clearDependencies(field) {
      this.lastUpdatedKey = field.name

      if (this.lastUpdatedKey === TEMPLATE_FIELD) {
        // reset files
        const customField = this.formFields.find(f => f.type === 'file')

        if (customField && this.shouldBeVisible(customField)) {
          this.FormModel[customField.name] = null
          delete this.FormModel[customField.name]
          this.options[field.key] = null
          delete this.options[field.key]
          this.$nextTick(() => {
            this.beforeCreateField(customField, true)
          })
        }
      }

      if (field.type === FIELD_TYPES.select) {
        Object.keys(this.FormModel).forEach(f => {
          const customField = this.formFields.find(f => ~f.dataSource.queryParameters.indexOf(field.name))
          if (customField) {
            this.FormModel[customField.name] = null
            delete this.FormModel[customField.name]
          }
        })
      }
    },

    beforeCreateField(field, force) {
      if (force === undefined && (!this.shouldBeVisible(field) || field.dataSource.url === '')) return

      // File fields
      if (field.type === FIELD_TYPES.file || field.type === 'file') {
        this.api.get(field.dataSource.url + '?' + this.query(field, this.FormModel)).then(options => {
          if (options && options.data) {
            this.FormModel[field.name] = this.mapAttachments(options.data)
            this.options[field.key] = options.data
          }
          if (force) {
            this.$forceUpdate()
          }
        })
      }

      if (field.integer) {
        if (this.lastUpdatedKey === TEMPLATE_FIELD || !this.FormModel[TEMPLATE_FIELD]) {
          this.loading = true
          this.api.get(field.dataSource.url + '?' + this.query(field, this.FormModel)).then(options => {
            if (options && options.data) {
              this.$set(this.FormModel, field.key, options.data)
            }
            this.loading = false
          })
        }
      }
    },

    query(field, form) {
      let query = ''

      field.dataSource.queryParameters.map(param => {
        const value = (form[param] || {}).text || form[param]
        query += value ? `&${param}=${encodeURIComponent(value)}` : ''
      })

      return query
    },

    fieldOptions(field) {
      if (!this.shouldBeVisible(field)) return []

      if (this.options[field.key]) return field.options || this.options[field.key] || []

      // this.options[field.key] ||
      this.$set(this.options, field.key, [])

      // File fields
      if (field.type === FIELD_TYPES.file) {
        this.$set(this.FormModel, field.key, [])
      }
    },

    multiple(field) {
      return field.type === FIELD_TYPES.file
    },

    mapAttachments(files) {
      let attachments = []
      for (var f in files) {
        if (files.hasOwnProperty(f)) {
          let entry = files[f]
          attachments.push({
            fileObject: true,
            id: entry.id,
            name: entry.name,
            file: entry.file,
            path: entry.path,
            type: entry.type,
            size: entry.size * 1024,
            success: true,
            readonly: true
          })
        }
      }
      return attachments
    },

    mapToSend(o) {
      let output = []

      Object.keys(o).forEach(k => {
        const customField = this.formFields.find(field => field.name === k) || {}
        output.push({
          name: k,
          multiple: customField.multiple,
          type: customField.type,
          values: o[k] ? o[k].map ? o[k].map(k => fileId(k)) : [o[k].text || o[k]] : []
        })
      })

      return output

      function fileId(file) {
        // upload endpoint returns application/json, so the upload component already
        // parsed file.response into an object; only JSON.parse when it's still a string
        let response = file.response
        if (typeof response === 'string') {
          try { response = JSON.parse(response) } catch (e) { response = {} }
        }
        return (response && response.id) || file.id
      }
    },

    onChange() {
      this.$v.$reset()
      this.$v.$touch()
    },

    validate() {
      this.$v.$touch()
      return !this.$v.$invalid
    },

    getFields(excludeStandardFields, justValues) {
      let result = []
      for (const [name, value] of Object.entries(this.FormModel)) {
        let field = this.formFields.filter(i => i.name === name)

        if (excludeStandardFields && field && field[0] && field[0].isStandard) continue

        let valueToSend = value
        if (justValues && valueToSend['text']) valueToSend = valueToSend['text']

        result.push({
          key: field && field[0] ? field[0].key : name,
          formKey: this.formSettings.key,
          name: name,
          value: valueToSend
        })
      }
      return result
    },

    getFieldLabel(name) {
      if (this.formFields) {
        let field = this.formFields.filter(f => f.name === name)
        return field && field[0] ? field[0].label : ''
      }
      return ''
    },

    getFieldIsRequired(name) {
      if (this.formFields) {
        let field = this.formFields.filter(f => f.name === name)
        return field && field[0] ? field[0].required : ''
      }
      return false
    },

    hideField(name) {
      if (this.formFields) {
        let field = this.formFields.filter(f => f.name === name)
        return field && field[0] ? field[0].hidden : false
      }
      return false
    }
  }
}
</script>

<style scoped lang='scss'>
@import "~@/assets/sass/_variables.scss";


.content >>> .dawn-page-section {
  .title-area .description.description {
    font-size: $font-size-base;
  }
}

.content >>> .form-group {

  &.invalid .dawnSelect[class], .form-control[class][error] {
    box-shadow: none;
  }
  input.error, .file-uploads[error] .btn {
    border-width: 1px;
  }

  .error-message {
    // display: none;
  }
  .optional-field-label {
    font-weight: $font-weight-light;
  }

  .disabled, .disabled + .alternate {
    opacity: 0.35;
  }

  .dawn-input-tooltip {
    padding: 0.5em;
    max-width: 310px;

    &:after {
      border-width: 10px;
      margin-top: -10px;
    }
    &:before {
      border-width: 11px;
      margin-top: -11px;
    }

    .small {
      font-size: 12px;
      margin-bottom: 0;
    }
  }
}

.field-enter-active, .field-leave-active {
  transition: opacity 0.5s;
}
.field-enter, .field-leave-to {
  opacity: 0;
  margin: -1px 0 0;
  height: 1px;
  overflow: hidden;
}

.info-field {
  margin: 0;
  padding: 1.5em 0;
  text-align: center;
  font-size: 15px;
  @include theme {
    color: v(border);
    border-top: 1px solid v(background-light);
    border-bottom: 1px solid v(background-light);
  }
}
</style>



// WEBPACK FOOTER //
// src/core/components/Form/customFields.vue